import { Eye } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../../api/client';
import type { MerchantRow, UiMerchantStatus } from '../../api/types';
import { MerchantProfile } from '../../components/merchants/MerchantProfile';
import { ChangeStatusModal } from '../../components/modals/ChangeStatusModal';
import { EditFeesModal } from '../../components/modals/EditFeesModal';
import { useAuth } from '../../context/AuthContext';
import {
  canChangeMerchantStatus,
  canEditMerchantFees,
  isCeo,
  isReadOnlyRole,
} from '../../utils/roles';
import { badgeClass, mapDbToUi, statusLabel } from '../../utils/merchantStatus';

export function MerchantsSection() {
  const { role } = useAuth();
  const ceo = isCeo(role);
  const readOnly = isReadOnlyRole(role);
  const [merchants, setMerchants] = useState<MerchantRow[]>([]);
  const [selected, setSelected] = useState<MerchantRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusTarget, setStatusTarget] = useState<MerchantRow | null>(null);
  const [feesTarget, setFeesTarget] = useState<MerchantRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listMerchants();
      setMerchants(res.merchants);
      setError(null);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateMerchantInList = useCallback((updated: MerchantRow) => {
    setMerchants((list) => list.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
    setSelected((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
  }, []);

  const saveStatus = async (merchantId: string, status: UiMerchantStatus) => {
    const res = await api.patchMerchantStatus(merchantId, status);
    if (res.merchant) updateMerchantInList(res.merchant);
    else await load();
  };

  const saveFees = async (merchantId: string, feePercent: number) => {
    const res = await api.patchMerchantFees(merchantId, feePercent);
    if (res.merchant) updateMerchantInList(res.merchant);
    else await load();
  };

  const openProfile = (m: MerchantRow) => setSelected(m);

  if (selected) {
    return (
      <MerchantProfile
        merchant={selected}
        onBack={() => setSelected(null)}
        onUpdated={updateMerchantInList}
      />
    );
  }

  return (
    <>
      <div className="section-header">
        <h1 className="console-title">Merchants</h1>
        <p className="console-sub">Directory · status &amp; fee controls</p>
      </div>

      {error ? <p className="form-error section-error">{error}</p> : null}

      <div className="table-wrap merchants-table">
        <div className="table-head data-table-head merchants-head">
          <div className="th col-name">Merchant</div>
          <div className="th col-wallet">Wallet</div>
          <div className="th col-fee">Fee %</div>
          <div className="th col-status">Status</div>
          <div className="th col-actions">Actions</div>
        </div>
        {loading ? (
          <div className="table-row merchants-row">
            <div className="col-span-all">Loading…</div>
          </div>
        ) : merchants.length === 0 ? (
          <div className="table-row merchants-row">
            <div className="col-span-all">No merchants</div>
          </div>
        ) : (
          merchants.map((m) => {
            const ui = mapDbToUi(m.status);
            return (
              <div
                key={m.id}
                className="table-row data-table-row merchants-row merchants-row-clickable"
                onClick={() => openProfile(m)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') openProfile(m);
                }}
                role="button"
                tabIndex={0}
              >
                <div className="col-name">
                  <div className="cell-primary">{m.name}</div>
                  <div className="cell-muted">{m.bank_iban || 'No IBAN'}</div>
                </div>
                <div className="col-wallet cell-mono">{m.solana_wallet}</div>
                <div className="col-fee">{m.fee_percent != null ? `${m.fee_percent}%` : '—'}</div>
                <div className="col-status">
                  <span className={badgeClass(ui)}>{statusLabel(ui)}</span>
                </div>
                <div className="col-actions row-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="btn-outline btn-sm btn-icon"
                    title="View profile"
                    onClick={() => openProfile(m)}
                  >
                    <Eye size={14} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="btn-outline btn-sm"
                    disabled={readOnly || (!ceo && !canChangeMerchantStatus(role))}
                    onClick={() => setStatusTarget(m)}
                  >
                    Status
                  </button>
                  <button
                    type="button"
                    className="btn-outline btn-sm"
                    disabled={readOnly || (!ceo && !canEditMerchantFees(role))}
                    onClick={() => setFeesTarget(m)}
                  >
                    Fees
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <ChangeStatusModal
        merchant={statusTarget}
        open={Boolean(statusTarget)}
        onClose={() => setStatusTarget(null)}
        onSave={saveStatus}
      />
      <EditFeesModal
        merchant={feesTarget}
        open={Boolean(feesTarget)}
        onClose={() => setFeesTarget(null)}
        onSave={saveFees}
      />
    </>
  );
}
